import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TableHead,
  Table,
  IconButton,
  Skeleton,
  InputAdornment,
  FormControl,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import UseFetchLedger from "../../helper/UseFetchLedger";
import { headingStyle } from "../../style/LapPageStyle";

const mono = `'Consolas','Courier New',monospace`;

const LedgerEntries = ({ onClose, id }) => {
  const [startDate, setStartDate] = useState(
    dayjs().tz("Asia/Kolkata").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(
    dayjs().tz("Asia/Kolkata").format("YYYY-MM-DD"),
  );

  const { data: ledgerEntriesList, isLoading } = UseFetchLedger(
    id,
    startDate,
    endDate,
  );

  const ledger = ledgerEntriesList?.payload?.ledger_entries || null;

  const rows = Array.isArray(ledger?.payment_history)
    ? ledger.payment_history
    : [];

  // optional: highlight row like screenshot (selected)
  const [activeRowId, setActiveRowId] = useState(null);

  const formatMoney = (val) => {
    if (val === null || val === undefined || val === "") return "";
    const num = Number(val);
    if (Number.isNaN(num)) return String(val);
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const totals = useMemo(() => {
    const debitTotal = rows.reduce((s, r) => s + (Number(r.debit) || 0), 0);
    const creditTotal = rows.reduce((s, r) => s + (Number(r.credit) || 0), 0);
    return { debitTotal, creditTotal };
  }, [rows]);

  const nowStr = dayjs().format("DD-MM-YYYY ddd | HH:mm");

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  // const totals = useMemo(() => {
  //   const debitTotal = rows.reduce((s, r) => s + (Number(r.debit) || 0), 0);
  //   const creditTotal = rows.reduce((s, r) => s + (Number(r.credit) || 0), 0);
  //   const openingBalance = Number(ledger?.opening_balance) || 0;

  //   const closingBalance = openingBalance + creditTotal - debitTotal;

  //   return { debitTotal, creditTotal, openingBalance, closingBalance };
  // }, [rows, ledger?.opening_balance]);

  return (
    <Box sx={{ width: "100%", p: 1 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        position="sticky"
        mt={-2}
        mb={1}
      >
        <Box />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Typography
            variant="heading_Large"
            fontWeight={700}
            gutterBottom
            sx={headingStyle}
          >
            Ledger Entries
          </Typography>
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon size="small" sx={{ color: "red" }} />
        </IconButton>
      </Box>

      <Box sx={{ gap: 2, display: "flex", flexWrap: "wrap", mb: 2 }}>
        {/* Start Date */}
        <FormControl variant="outlined" sx={{ width: "160px" }}>
          <TextField
            type="date"
            label="Start Date"
            value={startDate}
            onChange={handleStartDateChange}
            inputProps={{ max: endDate }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "40px",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#F7F7FF",
                fontWeight: "600",
              },
              "& fieldset": { border: "none" },
              cursor: "pointer",
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </FormControl>

        {/* End Date */}
        <FormControl variant="outlined" sx={{ width: "160px" }}>
          <TextField
            type="date"
            label="End Date"
            value={endDate}
            onChange={handleEndDateChange}
            inputProps={{
              min: startDate,
              max: new Date().toISOString().split("T")[0],
            }}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "40px",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#F7F7FF",
                fontWeight: "600",
              },
              "& fieldset": { border: "none" },
              cursor: "pointer",
            }}
          />
        </FormControl>
      </Box>

      {/* PAPER */}
      <Box
        sx={{
          height: "100%",
          bgcolor: "#f8fbff",
          border: "1px solid #9fb6d8",
          borderRadius: "4px",
          overflow: "hidden",
          fontFamily: mono,
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: "1px solid #9fb6d8",
            bgcolor: "#eef5ff",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* left header */}
          <Box sx={{ minWidth: 260 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}>
              Ledger of : {ledger?.name || "—"}
            </Typography>
            <Typography sx={{ fontSize: 11, lineHeight: 1.2, opacity: 0.9 }}>
              Address : {ledger?.address || "-"}
            </Typography>
            <Typography sx={{ fontSize: 11, lineHeight: 1.2, opacity: 0.9 }}>
              Phone : {ledger?.phone || "-"}
            </Typography>
            <Typography sx={{ fontSize: 11, lineHeight: 1.2, opacity: 0.9 }}>
              Date : {ledger?.date || `${startDate} to ${endDate}`}
            </Typography>
          </Box>

          {/* right header */}
          <Box sx={{ textAlign: "right" }}>
            {/* <Typography sx={{ fontSize: 11, lineHeight: 1.2 }}>
              {nowStr}
            </Typography> */}
            {/* <Typography sx={{ fontSize: 11, lineHeight: 1.2 }}>
              Period : {ledger?.date || `${startDate} - ${endDate}`}
            </Typography> */}
          </Box>
        </Box>

        {/* TABLE */}
        <Box sx={{ height: "calc(100% - 120px)", overflow: "auto" }}>
          <TableContainer>
            <Table
              size="small"
              sx={{
                "& th, & td": {
                  borderRight: "1px solid #9fb6d8",
                },
                "& th:last-child, & td:last-child": {
                  borderRight: "none",
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#f1f6ff" }}>
                  {[
                    "Date & Time",
                    "Type",
                    "Narration",
                    "Debit",
                    "Credit",
                    "Balance",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        py: 0.7,
                        borderBottom: "1px solid #9fb6d8",
                        textAlign: h === "Narration" ? "left" : "center",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              {isLoading ? (
                <TableBody>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={6} sx={{ p: 0 }}>
                        <Skeleton variant="rectangular" height={26} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ textAlign: "center", py: 3, fontSize: 12 }}
                      >
                        No ledger entries found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => {
                      const active = activeRowId === r.id;
                      return (
                        <TableRow
                          key={r.id}
                          onClick={() => setActiveRowId(r.id)}
                          sx={{
                            cursor: "pointer",
                            bgcolor: active ? "#0b3d91" : "transparent",
                            "& td": { color: active ? "#fff" : "#111" },
                            "&:hover": {
                              bgcolor: active ? "#0b3d91" : "#eaf2ff",
                            },
                          }}
                        >
                          <TableCell
                            sx={{ fontSize: 11, py: 0.6, textAlign: "center" }}
                          >
                            {r.date || ""}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 11,
                              py: 0.6,
                              textAlign: "left",
                              textTransform: "capitalize",
                            }}
                          >
                            {r.entry_type?.split("_").join(" ") || ""}
                          </TableCell>

                          {/* narration like screenshot: left aligned */}
                          <TableCell
                            sx={{ fontSize: 11, py: 0.6, textAlign: "left" }}
                          >
                            {r.narration || ""}
                          </TableCell>

                          <TableCell
                            sx={{ fontSize: 11, py: 0.6, textAlign: "left" }}
                          >
                            {formatMoney(r.debit)}
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: 11, py: 0.6, textAlign: "left" }}
                          >
                            {formatMoney(r.credit)}
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: 11, py: 0.6, textAlign: "left" }}
                          >
                            {formatMoney(r.balance)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Box>

        {/* FOOTER (like screenshot) */}
        <Box
          sx={{
            borderTop: "1px solid #9fb6d8",
            px: 1.5,
            py: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "stretch",
            gap: 2,
            bgcolor: "#f7fbff",
          }}
        >
          {/* left footer note */}
          <Box sx={{ flex: 1 }}>
            {/* <Typography
              sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.2 }}
            >
              LOCAL PURCHASE TAX PAID
            </Typography> */}
            {/* <Typography sx={{ fontSize: 11, opacity: 0.9 }}>
              Bill No. : {rows?.[0]?.bill_no || "—"} &nbsp; Dt. :{" "}
              {rows?.[0]?.date || "—"}
            </Typography> */}
          </Box>

          {/* right footer totals box */}
          <Box
            sx={{
              width: 260,
              border: "1px solid #9fb6d8",
              bgcolor: "#ffffff",
              px: 1,
              py: 0.8,
            }}
          >
            {[
              ["Opening", formatMoney(ledger?.opening_balance || " ")],
              ["Total (Cr)", formatMoney(totals?.creditTotal || " ")],
              ["Total (Dr)", formatMoney(totals?.debitTotal || " ")],
              ["Closing", formatMoney(ledger?.closing_balance || " ")],
            ].map(([k, v]) => (
              <Box
                key={k}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 11,
                  py: 0.2,
                }}
              >
                <span>{k} :</span>
                <span>{v}</span>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LedgerEntries;
